import { browser, dev } from "$app/environment";

const OPENAI_ADS_SCRIPT_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const OPENAI_ADS_SCRIPT_SELECTOR = 'script[data-openai-ads-pixel="true"]';
const GDPR_COOKIE_NAME = "beyonk_gdpr";
const OPENAI_ATTRIBUTION_COOKIE_NAME = "__oppref";

let trackingConsentGranted = false;
let isInitialised = false;

/**
 * Read the advertising/tracking consent stored by the GDPR banner.
 *
 * @returns {boolean}
 */
export function hasOpenAIAdsTrackingConsent() {
    if (!browser || !trackingConsentGranted) return false;

    try {
        const cookie = document.cookie
            .split("; ")
            .find((row) => row.startsWith(`${GDPR_COOKIE_NAME}=`));
        if (!cookie) return false;

        const value = JSON.parse(atob(cookie.slice(`${GDPR_COOKIE_NAME}=`.length)));
        return value?.choices?.tracking === true;
    } catch (error) {
        if (dev) {
            console.warn("[OpenAI Ads] Failed to parse tracking consent cookie:", error);
        }
        return false;
    }
}

/**
 * Keep the in-memory consent state synchronized with the cookie banner.
 *
 * The SDK cannot be unloaded after consent is withdrawn, so every measurement
 * call checks this state and the current consent cookie before sending.
 *
 * @param {boolean} granted
 */
export function setOpenAIAdsTrackingConsent(granted) {
    trackingConsentGranted = granted;

    if (!browser || granted) return;

    const script = document.querySelector(OPENAI_ADS_SCRIPT_SELECTOR);
    if (script instanceof HTMLScriptElement && script.dataset.openaiAdsLoaded !== "true") {
        script.remove();
    }

    if (window.oaiq?.q) {
        window.oaiq.q.length = 0;
    }

    // The SDK stores the click reference in this first-party cookie.
    // biome-ignore lint/suspicious/noDocumentCookie: Consent withdrawal must expire vendor state.
    document.cookie = `${OPENAI_ATTRIBUTION_COOKIE_NAME}=; Max-Age=0; path=/; SameSite=Lax`;
    isInitialised = false;
}

/**
 * Create the command queue used until the OpenAI Ads SDK has loaded.
 *
 * @returns {NonNullable<Window["oaiq"]>}
 */
function createOpenAIAdsQueue() {
    function oaiqQueue() {
        // biome-ignore lint/complexity/noArguments: The OpenAI SDK queue follows the vendor snippet.
        oaiqQueue.q.push(arguments);
    }
    oaiqQueue.q = /** @type {IArguments[]} */ ([]);
    return oaiqQueue;
}

/**
 * Initialise the OpenAI Ads Measurement Pixel after tracking consent.
 *
 * @param {{ pixelId: string, debug?: boolean }} options
 * @returns {boolean} Whether the pixel is initialised or already available.
 */
export function initOpenAIAdsPixel({ pixelId, debug = false }) {
    if (!browser || !pixelId || !hasOpenAIAdsTrackingConsent()) return false;
    if (isInitialised) return true;

    if (!window.oaiq) {
        window.oaiq = createOpenAIAdsQueue();
    }

    if (!document.querySelector(OPENAI_ADS_SCRIPT_SELECTOR)) {
        const script = document.createElement("script");
        script.async = true;
        script.src = OPENAI_ADS_SCRIPT_URL;
        script.dataset.openaiAdsPixel = "true";
        script.addEventListener("load", () => {
            script.dataset.openaiAdsLoaded = "true";
        });
        document.head.appendChild(script);
    }

    window.oaiq("init", {
        pixelId,
        ...(debug ? { debug: true } : {}),
    });
    isInitialised = true;

    return true;
}

/**
 * Record a page view for OpenAI Ads traffic measurement.
 *
 * No page URL, title, or other visitor data is added to the standard event.
 *
 * @returns {boolean} Whether the event was queued for measurement.
 */
export function trackOpenAIPageViewed() {
    if (!browser || !isInitialised || !hasOpenAIAdsTrackingConsent() || !window.oaiq) {
        return false;
    }

    window.oaiq("measure", "page_viewed", {
        type: "contents",
    });

    return true;
}

/**
 * Record a successful contact/enquiry submission.
 *
 * No form values or other personal data are sent to OpenAI.
 *
 * @returns {boolean} Whether the event was queued for measurement.
 */
export function trackOpenAILeadCreated() {
    if (!browser || !isInitialised || !hasOpenAIAdsTrackingConsent() || !window.oaiq) {
        return false;
    }

    window.oaiq("measure", "lead_created", {
        type: "customer_action",
    });

    return true;
}
